import {useCallback, useEffect, useMemo, useState} from "react"
import {useEmpresasList, normalizeDoc, defaultGetLabel} from "./useEmpresasList"

const defaultFieldState = {value: "", inputValue: "", invalid: false, pendingResolution: false}

const normalizeFilterValue = (val) => {
    const s = `${val || ""}`
    const lower = s.toLowerCase()
    const digits = s.replace(/\D/g, "")
    return `${lower} ${digits}`.trim()
}

/**
 * Controlador de autocomplete para empresas.
 * Mantém estado por campo (key) e expõe handlers compatíveis com InputAutoComplete.
 */
export const useEmpresasAutocompleteController = (reloadKey = 0) => {
    const {options, loading} = useEmpresasList(true, reloadKey)
    const [stateMap, setStateMap] = useState({})

    const getState = useCallback(
        (key) => stateMap[key] || defaultFieldState,
        [stateMap]
    )

    const getLabel = useCallback((option) => defaultGetLabel(option), [])

    const findOption = useCallback(
        (rawVal) => {
            const target = normalizeDoc(typeof rawVal === "string" ? rawVal : rawVal?.value || rawVal)
            return options.find((opt) => normalizeDoc(opt.value) === target)
        },
        [options]
    )

    const findByLooseMatch = useCallback(
        (raw) => {
            if (!options || !options.length) return null
            const rawStr = typeof raw === "object" ? raw?.value || raw?.label || "" : raw
            const doc = normalizeDoc(rawStr)
            const rawLower = `${rawStr || ""}`.trim().toLowerCase()
            if (!rawLower && !doc) return null
            return options.find((opt) => {
                const optDoc = normalizeDoc(opt.value)
                const optLabel = `${opt.label || ""}`.toLowerCase()
                const docMatch = doc && optDoc === doc
                const labelMatch = rawLower && optLabel.includes(rawLower)
                return docMatch || labelMatch
            }) || null
        },
        [options]
    )

    const isValid = useCallback(
        (raw) => Boolean(findByLooseMatch(raw)),
        [findByLooseMatch]
    )

    const updateState = useCallback((key, updater) => {
        setStateMap((prev) => {
            const current = prev[key] || defaultFieldState
            const nextState = updater(current)
            if (
                current.value === nextState.value &&
                current.inputValue === nextState.inputValue &&
                current.invalid === nextState.invalid &&
                current.pendingResolution === nextState.pendingResolution
            ) {
                return prev
            }
            return {...prev, [key]: nextState}
        })
    }, [])

    const handleChange = useCallback(
        (key, _event, newValue, reason) => {
            void _event
            updateState(key, (current) => {
                if (newValue?.isNoOption) return current
                const normalized = normalizeDoc(typeof newValue === "string" ? newValue : newValue?.value || newValue)
                if (reason === "clear" || (!normalized && !newValue)) {
                    return defaultFieldState
                }
                const matched = findOption(normalized)
                const labelText = matched ? getLabel(matched) : getLabel(newValue) || normalized
                if (reason === "selectOption") {
                    return {value: normalized, inputValue: labelText, invalid: false, pendingResolution: false}
                }
                return {value: normalized, inputValue: labelText, invalid: current.invalid, pendingResolution: false}
            })
        },
        [findOption, getLabel, updateState]
    )

    const handleInputChange = useCallback(
        (key, _event, newInput) => {
            void _event
            updateState(key, () => {
                const normalized = normalizeDoc(newInput)
                if (!newInput?.trim?.().length) {
                    return {value: "", inputValue: "", invalid: false, pendingResolution: false}
                }
                return {value: normalized, inputValue: newInput, invalid: false, pendingResolution: false}
            })
        },
        [updateState]
    )

    const handleBlur = useCallback(
        (key) => {
            updateState(key, (current) => {
                const raw = current.inputValue || current.value || ""
                if (!raw.trim().length) return defaultFieldState
                // durante carregamento, não marca erro; mantém valor normalizado
                if (!options || options.length === 0) {
                    return {
                        value: normalizeDoc(raw),
                        inputValue: current.inputValue || raw,
                        invalid: false,
                        pendingResolution: true,
                    }
                }
                const match = findOption(raw) || findByLooseMatch(raw)
                if (match) {
                    return {
                        value: normalizeDoc(match.value),
                        inputValue: getLabel(match),
                        invalid: false,
                        pendingResolution: false,
                    }
                }
                const validMatch = findByLooseMatch(raw)
                const valid = Boolean(validMatch)
                return {
                    value: validMatch ? normalizeDoc(validMatch.value) : normalizeDoc(raw),
                    inputValue: validMatch ? getLabel(validMatch) : raw,
                    invalid: !valid,
                    pendingResolution: false,
                }
            })
        },
        [findByLooseMatch, findOption, getLabel, options, updateState]
    )

    const syncValue = useCallback(
        (key, externalValue, externalLabel) => {
            updateState(key, () => {
                const normalized = normalizeDoc(externalValue)
                if (!normalized && !externalLabel) return defaultFieldState
                const hasOptions = Boolean(options && options.length)
                const match = hasOptions ? (findOption(normalized || externalLabel) || findByLooseMatch(normalized || externalLabel)) : null
                const inputText = externalLabel || (match ? getLabel(match) : externalValue || "")
                if (!hasOptions && normalized) {
                    return {
                        value: normalized,
                        inputValue: inputText,
                        invalid: false,
                        pendingResolution: true,
                    }
                }
                const valid = normalized ? isValid(externalValue || normalized) || Boolean(match) : false
                return {
                    value: normalized,
                    inputValue: inputText,
                    invalid: normalized ? !valid : false,
                    pendingResolution: false,
                }
            })
        },
        [findByLooseMatch, findOption, getLabel, isValid, options, updateState]
    )

    const meta = useMemo(
        () => ({
            options,
            loading,
            resolved: Boolean(options && options.length),
            freeSolo: true,
            getOptionLabel: getLabel,
            normalizeValue: normalizeDoc,
            filterKeys: ["label", "value", "search"],
            normalizeFilter: normalizeFilterValue,
            filterOptions: (opts, params) => {
                const queryRaw = params?.inputValue || ""
                const queryLower = `${queryRaw}`.toLowerCase().trim()
                const queryDigits = normalizeDoc(queryRaw)
                if (!queryLower && !queryDigits) return []

                const matches = (opts || []).filter((opt) => {
                    if (!opt) return false
                    const label = `${opt.label || ""}`.toLowerCase()
                    const valueDigits = normalizeDoc(opt.value)
                    const search = `${opt.search || ""}`.toLowerCase()
                    const labelMatch = queryLower && (label.includes(queryLower) || search.includes(queryLower))
                    const docMatch = queryDigits && valueDigits.includes(queryDigits)
                    return labelMatch || docMatch
                })

                if (!matches.length) {
                    return [
                        {
                            label: `Não cadastrado ${queryRaw}`,
                            value: "__no_option__",
                            isNoOption: true,
                        },
                    ]
                }

                return matches
            },
            minCharsToFilter: 1,
            noOptionsText: (input) =>
                input ? `Não cadastrado ${input}` : "Nenhum resultado encontrado",
            getOptionDisabled: (opt) => Boolean(opt?.isNoOption),
        }),
        [getLabel, loading, options]
    )

    useEffect(() => {
        Object.entries(stateMap).forEach(([key, current]) => {
            updateState(key, () => {
                const raw = `${current.inputValue || current.value || ""}`.trim()
                if (!raw.length) return defaultFieldState

                const match = findOption(current.value || current.inputValue)
                if (match) {
                    return {
                        value: normalizeDoc(match.value),
                        inputValue: getLabel(match),
                        invalid: false,
                    }
                }
                if (!options || !options.length) {
                    if (current.pendingResolution) {
                        return {...current, invalid: false}
                    }
                    return current
                }
                if (current.pendingResolution) {
                    const resolvedMatch = findOption(raw) || findByLooseMatch(raw)
                    if (resolvedMatch) {
                        return {
                            value: normalizeDoc(resolvedMatch.value),
                            inputValue: getLabel(resolvedMatch),
                            invalid: false,
                            pendingResolution: false,
                        }
                    }
                    return {...current, invalid: true, pendingResolution: false}
                }
                const valid = isValid(raw)
                return {...current, invalid: !valid}
            })
        })
    }, [findOption, findByLooseMatch, getLabel, isValid, options, stateMap, updateState])

    const actions = useMemo(
        () => ({
            setInputValue: (key, inputValue) =>
                updateState(key, (current) => ({
                    ...current,
                    inputValue: inputValue || "",
                })),
            setSelectedValue: (key, value) =>
                updateState(key, (current) => ({
                    ...current,
                    value: normalizeDoc(value),
                    invalid: false,
                })),
            reset: () => setStateMap({}),
        }),
        [updateState]
    )

    const computed = useCallback(
        (key) => {
            const current = stateMap[key] || defaultFieldState
            return {
                hasError: Boolean(current.invalid),
                isLoading: Boolean(loading || current.pendingResolution),
                hasValue: Boolean(current.value || current.inputValue),
                value: current.value || "",
                inputValue: current.inputValue || "",
            }
        },
        [loading, stateMap]
    )

    const handlers = useMemo(
        () => ({
            onChange: handleChange,
            onInputChange: handleInputChange,
            onBlur: handleBlur,
            syncValue,
        }),
        [handleBlur, handleChange, handleInputChange, syncValue]
    )

    return useMemo(
        () => ({
            state: {byKey: stateMap},
            getState,
            actions,
            handlers,
            meta,
            computed,
            cleanup: actions.reset,
        }),
        [actions, computed, getState, handlers, meta, stateMap]
    )
}
